import { postCheckAndVerifyPhone } from '~/api/mitra';
import {
  validateKtp,
  validateSelfie,
  validateSupportingDocument as validateSupportingDocumentApi,
} from '~/api/chat/ai-models';
import { retrieveDanaProfile } from '~/api/dana-wallet';
import { getMe } from '~/api/agent';
import { getInvoices } from '~/api/invoices';
import { getCredentialChangeStatus } from '~/api/action-logs';
import { changePhoneNumberUsingIdentityProofs } from '~/api/chat/chatbot';
import { updateConfirmedPhoneNumber } from '~/api/user';
import CPN_ERROR_TYPES from '~/utils/cpn-error-types';
import {
  fetchDanaProfile,
  updateDanaBound as updateDanaBoundApi,
} from '~/api/wallet';

const dataUrlToFile = async (dataUrl, fileName) => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const ext = dataUrl.split(';')[0].split('/')[1];
  return new File([blob], `${fileName}.${ext}`, { type: `image/${ext}` });
};

export const state = () => ({
  formData: {
    name: null,
    old_phone: null,
    email: null,
    selectedOptions: null,
    new_phone: null,
    reason: null,
  },
  imageData: {
    ktp: '',
    selfie: '',
    doc: '',
    docUpload: '',
  },
  otpFailedRetryAttempt: false,
  ktpAndSelfieValidation: {
    loading: false,
    data: null,
    error: null,
  },
  supportingDocumentValidation: {
    loading: false,
    data: null,
    error: null,
  },
  changePhoneNumber: {
    loading: false,
    data: null,
    error: null,
  },

  /**
   * This will help to decide whether the user should be directly handled by CS agent or by Acha
   * Possible values - document_unsupported_clear | document_unsupported_unclear | document_supported_clear
   * document_unsupported_clear - directly chat with CS agent
   * document_unsupported_unclear - error snackbar, session won't be started
   * document_supported_clear - chat with Acha
   */
  supportingDocumentStatus: '',

  checkUserSaldo: {
    loading: false,
    data: null,
    error: null,
  },

  checkUserCredentialChangesHistory: {
    loading: false,
    data: null,
    error: null,
  },
  token: '',
  newPhoneNumber: '',
  phoneNumberState: {
    isNumberTaken: false,
    message: '',
  },
  requestOrigin: '',
  unbindState: false,
});

export const getters = {
  getRequiredImage(state) {
    return state.imageData;
  },
  getChangePhoneFormData(state) {
    return state.formData;
  },
  isDataValid(state) {
    const { selectedOptions, name } = state.formData;
    // const isPhoneDataValid = !!old_phone && !!new_phone;
    const isOtherDataValid = !!selectedOptions && !!name;
    const isLocalStorageDataValid = process.browser ? window.localStorage.getItem('change_phone_number_form') : false;

    return isOtherDataValid || isLocalStorageDataValid;
  },
  getFormData(state) {
    const localFormData = window.localStorage.getItem('change_phone_number_form');

    const changePhoneFormData = {
      formData: state.formData.new_phone ? state.formData : JSON.parse(localFormData),
      imageData: {
        ktp: state.imageData.ktp || window.localStorage.getItem('change_phone_number_img_ktp'),
        selfie: state.imageData.selfie || window.localStorage.getItem('change_phone_number_img_selfie'),
        doc: state.imageData.doc || window.localStorage.getItem('change_phone_number_img_doc'),
        docUpload: state.imageData.docUpload || window.localStorage.getItem('change_phone_number_img_docUpload'),
      },
      otpFailedRetryAttempt: state.otpFailedRetryAttempt,
    };

    return changePhoneFormData;
  },

  getKtpAndSelfieValidation(state) {
    return state.ktpAndSelfieValidation;
  },

  getSupportingDocumentValidation(state) {
    return state.supportingDocumentValidation;
  },

  getChangePhoneNumberState(state) {
    return state.changePhoneNumber;
  },

  getSupportingDocumentStatus(state) {
    return state.supportingDocumentStatus;
  },

  getUserSaldoWalletEligibility(state) {
    return state.checkUserSaldo;
  },

  getUserCredentialChangesHistoryEligibility(state) {
    return state.checkUserCredentialChangesHistory;
  },

  getChangePhoneToken(state) {
    return state.token;
  },

  getPhoneNumberState(state) {
    return state.phoneNumberState;
  },

  getNewPhoneNumber(state) {
    return state.newPhoneNumber;
  },

  getRequestOrigin(state) {
    return state.requestOrigin;
  },

  getUnbindState(state) {
    return state.unbindState;
  }
};

export const mutations = {
  setImageData(state, payload = { type, img }) {
    state.imageData[payload.type] = payload.img;
  },
  setPhoneData(state, payload = { old_phone, new_phone }) {
    state.formData[payload.old_phone] = payload.old_phone;
    state.formData[payload.new_phone] = payload.new_phone;
  },
  setFormData(state, payload) {
    state.formData = payload;
  },
  setOTPFailedRetryAttempt(state, payload) {
    state.otpFailedRetryAttempt = payload;
  },
  clearFormData(state) {
    state.formData = {
      name: null,
      old_phone: null,
      email: null,
      selectedOptions: null,
      new_phone: null,
      reason: null,
    };
    state.imageData = {
      ktp: '',
      selfie: '',
      doc: '',
      docUpload: '',
    };
  },

  // KTP and Selfie validation
  beginValidateKtpAndSelfie(state) {
    state.ktpAndSelfieValidation = {
      loading: true,
      data: null,
      error: null,
    };
  },
  successValidateKtpAndSelfie(state) {
    state.ktpAndSelfieValidation = {
      loading: false,
      data: true,
      error: null,
    };
  },
  errorValidateKtpAndSelfie(state, { name = '', message = '' }) {
    state.ktpAndSelfieValidation = {
      loading: false,
      data: null,
      error: { name, message },
    };
  },

  // Supporting document validation
  beginValidateSupportingDocument(state) {
    state.supportingDocumentValidation = {
      loading: true,
      data: null,
      error: null,
    };
  },
  successValidateSupportingDocument(state) {
    state.supportingDocumentValidation = {
      loading: false,
      data: true,
      error: null,
    };
  },
  errorValidateSupportingDocument(state, { name = '', message = '' }) {
    state.supportingDocumentValidation = {
      loading: false,
      data: null,
      error: { name, message },
    };
  },

  setSupportingDocumentStatus(state, status = '') {
    state.supportingDocumentStatus = status;
  },

  // check user saldo
  beginCheckUserSaldo(state) {
    state.checkUserSaldo = {
      loading: false,
      data: null,
      error: false,
    };
  },
  successCheckUserSaldo(state, { data }) {
    state.checkUserSaldo = {
      loading: false,
      data,
      error: false,
    };
  },
  errorCheckUserSaldo(state, { error }) {
    state.checkUserSaldo = {
      loading: false,
      data: null,
      error,
    };
  },

  // credential change history
  beginCheckUserCredentialChangesHistory(state) {
    state.checkUserCredentialChangesHistory = {
      loading: false,
      data: null,
      error: false,
    };
  },
  successCheckUserCredentialChangesHistory(state, { data }) {
    state.checkUserCredentialChangesHistory = {
      loading: false,
      data,
      error: false,
    };
  },

  // Automatic Change Phone Number after verifying identity proofs
  beginChangePhoneNumber(state) {
    state.changePhoneNumber = {
      loading: true,
      data: null,
      error: null,
    };
  },
  successChangePhoneNumber(state, { data }) {
    state.changePhoneNumber = {
      loading: false,
      data,
      error: null,
    };
  },
  errorChangePhoneNumber(state, { name = '', message = '' }) {
    state.changePhoneNumber = {
      loading: false,
      data: null,
      error: { name, message },
    };
  },
  setChangePhoneToken(state, val) {
    state.token = val;
  },
  setPhoneNumberState(state, { flag, message }) {
    state.phoneNumberState = {
      isNumberTaken: flag,
      message,
    };
  },
  setNewPhoneNumber(state, val) {
    state.newPhoneNumber = val;
  },
  setRequestOrigin(state, val) {
    state.requestOrigin = val;
  },
  setUnbindState(state, flag) {
    state.unbindState = flag;
  },
};

export const actions = {
  setImage({ commit }, payload) {
    commit('setImageData', payload);
  },
  setChangePhoneFormData({ commit }, payload) {
    commit('setFormData', payload);
  },
  saveToLocalStorage({ state }) {
    const { imageData } = state;
    if (state.formData.old_phone) {
      window.localStorage.setItem('change_phone_number_form', JSON.stringify(state.formData));
    }
    window.localStorage.setItem('change_phone_number_img_ktp', imageData.ktp);
    window.localStorage.setItem('change_phone_number_img_selfie', imageData.selfie);
    window.localStorage.setItem('change_phone_number_img_doc', imageData.doc);
    window.localStorage.setItem('change_phone_number_img_docupload', imageData.docUpload);
  },
  clearLocalStorage() {
    window.localStorage.removeItem('change_phone_number_form');
    window.localStorage.removeItem('change_phone_number_img_ktp');
    window.localStorage.removeItem('change_phone_number_img_selfie');
    window.localStorage.removeItem('change_phone_number_img_doc');
    window.localStorage.removeItem('change_phone_number_img_docupload');
  },
  async checkAndVerifyPhone({ commit, dispatch }, payload = { old_phone, new_phone }) {
    commit('setOTPFailedRetryAttempt', false);
    dispatch('otp/setOtpLoadingState', true, { root: true });
    return await postCheckAndVerifyPhone(this.$axios, payload)
      .then(() => {
        commit('setPhoneData', payload);
        return true;
      })
      .catch(err => {
        if (err.response) {
          const { data } = err.response;
          const otpResponseMessage = data.errors ? data.errors[0].message : 'unknown error';
          const isPhoneValid =
            otpResponseMessage.includes('Metode OTP tidak ada') ||
            otpResponseMessage.includes('Token OTP berhasil dikirimkan');
          const isOTPRetrievalFailed =
            otpResponseMessage.includes('Gagal mengirimkan token') || otpResponseMessage.includes('unknown error');

          dispatch('otp/setOtpValidationState', true, { root: true });

          if (isOTPRetrievalFailed) {
            commit('setOTPFailedRetryAttempt', true);
          }
          if (isPhoneValid) {
            dispatch('otp/startOtp', { method: 'sms', timer: data.meta.wait_time }, { root: true });
          }
          return isPhoneValid;
        }
        commit('setOTPFailedRetryAttempt', true);
        return false;
      });
  },

  /**
   * Create a formatted text message to be sent automatically later
   */
  createAutomaticMessage({ getters, rootGetters }) {
    const formState = getters.getFormData;
    if (getters.isDataValid) {
      const { formData } = formState;
      const name = formData.name ? formData.name : rootGetters['user/getMe'].name;
      const reasonForm = formData.reason ? formData.reason : '';

      const automaticMessage = `Halo CS Mitra Bukalapak
        Saya mau ubah nomor handphone saya di Mitra Bukalapak. Berikut informasi saya:

        Nama:
        ${name}

        No. handphone lama:
        ${formData.old_phone}

        No. handphone baru:
        ${formData.new_phone}

        Alasan:
        ${formData.selectedOptions}
        ${reasonForm}

        Berikut juga terlampir foto KTP, selfie dengan KTP dan dokumen pendukung.`;

      return {
        name,
        text: automaticMessage,
      };
    }

    return false;
  },

  /**
   * Dedicated action to create file upload (KTP, selfie, supporting document)
   */
  async createAutomaticDocumentMessages({ getters }) {
    const formState = getters.getFormData;
    if (getters.isDataValid) {
      const { imageData } = formState;
      const imageDocData = imageData.docUpload ? imageData.docUpload : imageData.doc;
      const imageFile = {
        ktp: await dataUrlToFile(imageData.ktp, 'ktp'),
        selfie: await dataUrlToFile(imageData.selfie, 'selfie'),
        doc: await dataUrlToFile(imageDocData, 'dokumen'),
      };

      return { ...imageFile };
    }

    return false;
  },

  /**
   * Cleanup formData from the state and the local storage
   */
  flushMessageAndLocalStorage({ commit, dispatch }) {
    commit('clearFormData');
    dispatch('clearLocalStorage');
  },

  /**
   * Validate KTP and Selfie using AI API
   * @param {object} context
   * @param {object} payload
   * @param {string} payload.ktpImg - base64 string of KTP
   * @param {string} payload.selfieImg - base64 string of selfie
   * @param {function} payload.trackCpnKtpValidation - tracking function for KTP validation
   * @param {function} payload.trackCpnSelfieValidation - tracking function for Selfie validation
   * @returns {Promise<boolean>}
   */
  async validateKtpAndSelfie(
    { commit },
    { ktpImg = '', selfieImg = '', trackCpnKtpValidation = () => {}, trackCpnSelfieValidation = () => {} }
  ) {
    try {
      commit('beginValidateKtpAndSelfie');

      // KTP Validation
      const {
        data: [ktpVerificationData],
      } = await validateKtp(this.$axios, [
        {
          image_ktp: ktpImg.split(',')[1],
        },
      ]);
      if (!ktpVerificationData.ktp_accepted) {
        // Track KTP validation Not Accepted
        trackCpnKtpValidation({
          success: false,
          failedReason: 'KTP is not accepted',
        });
        throw new Error(CPN_ERROR_TYPES.ktp_invalid.name);
      }
      // Track KTP validation Accepted
      trackCpnKtpValidation({
        success: true,
      });

      // Selfie Validation
      const {
        data: [selfieVerificationData],
      } = await validateSelfie(this.$axios, [
        {
          image_self_portrait: selfieImg.split(',')[1],
        },
      ]);
      if (!selfieVerificationData.self_portrait_accepted) {
        // Track Selfie Validation Not Accepted
        trackCpnSelfieValidation({
          success: false,
          failedReason: 'Selfie is not accepted',
        });
        throw new Error(CPN_ERROR_TYPES.selfie_invalid.name);
      }
      // Track Selfie Validation Accepted
      trackCpnSelfieValidation({
        success: true,
      });

      commit('successValidateKtpAndSelfie');
      return true;
    } catch (error) {
      const selectedErrorCause = CPN_ERROR_TYPES[error.message] || CPN_ERROR_TYPES.ktp_and_selfie_validation_api_error;

      // Track KTP and Selfie validation error
      trackCpnKtpValidation({
        success: false,
        failedReason: 'Something went wrong when validating KTP',
      });
      trackCpnSelfieValidation({
        success: false,
        failedReason: 'Something went wrong when validating Selfie',
      });

      commit('errorValidateKtpAndSelfie', selectedErrorCause);
      throw new Error(selectedErrorCause.name);
    }
  },

  /**
   * Validate supporting document
   * @param {object} context
   * @param {object} payload
   * @param {string} payload.docImageSrc - base64 string of document picture
   * @param {function} payload.trackCpnSupportingDocumentValidation - tracking function for Supporting Document Validation
   * @returns {Promise<boolean>}
   */
  async validateSupportingDocument(
    { commit, dispatch },
    { docImageSrc = '', trackCpnSupportingDocumentValidation = () => {} }
  ) {
    try {
      commit('beginValidateSupportingDocument');

      const {
        data: [docVerificationData],
      } = await validateSupportingDocumentApi(this.$axios, [
        {
          image_base64: docImageSrc.split(',')[1],
        },
      ]);

      const {
        categories: [documentCategory],
        document_clear: documentClear,
      } = docVerificationData;

      /**
       * Case 1 - Unsupported document category and clear document
       * categories: paper_receipt | surat_jalan | invalid
       * document_clear: true
       * init chat with agent
       */
      if (['paper_receipt', 'surat_jalan', 'invalid'].includes(documentCategory.name) && documentClear) {
        commit('successValidateSupportingDocument');
        dispatch('setSupportingDocumentStatus', { status: 'document_unsupported_clear' });
        // Track Supporting Document validation success (Document valid)
        trackCpnSupportingDocumentValidation({
          success: true,
        });
        return true;
      }

      /**
       * Case 2 - Unsupported document category and unclear document
       * categories: paper_receipt | surat_jalan | invalid
       * document_clear: false
       * Show error snackbar
       */
      if (['paper_receipt', 'surat_jalan', 'invalid'].includes(documentCategory.name) && !documentClear) {
        dispatch('setSupportingDocumentStatus', { status: 'document_unsupported_unclear' });
        // Track Supporting Document validation error (Document invalid)
        trackCpnSupportingDocumentValidation({
          success: false,
          failedReason: 'Document is Unsupported and Unclear',
        });
        throw new Error(CPN_ERROR_TYPES.document_unsupported_unclear.name);
      }

      /**
       * Case 3 - Supported document type and clear document
       * categories: topup_ebanking | trx_mitra
       * document_clear: true
       * Begin chatbot session with Acha
       */
      commit('successValidateSupportingDocument');
      dispatch('setSupportingDocumentStatus', { status: 'document_supported_clear' });
      // Track Supporting Document validation success (Document valid)
      trackCpnSupportingDocumentValidation({
        success: true,
      });
      return true;
    } catch (error) {
      const selectedErrorCause =
        CPN_ERROR_TYPES[error.message] || CPN_ERROR_TYPES.supporting_document_validation_api_error;

      // Track Supporting Document validation error
      trackCpnSupportingDocumentValidation({
        success: false,
        failedReason: 'Something went wrong when validating Supporting Document',
      });
      commit('errorValidateSupportingDocument', selectedErrorCause);
      throw new Error(selectedErrorCause.name);
    }
  },

  /**
   * Check current user's Saldo
   * @param {object} context
   * @param {object} payload
   * @param {function} payload.trackCpnCheckWalletEligibility - tracking function for Wallet eligibility checking
   * @returns {Promise<object>}
   */
  async checkUserSaldo({ commit }, { trackCpnCheckWalletEligibility = () => {} }) {
    try {
      commit('beginCheckUserSaldo');
      // Get dana profile
      const { data: danaProfile } = await retrieveDanaProfile(this.$axios)
        .then(res => res)
        .catch(() => {
          return { data: null };
        });
      // Get agents me
      const { data: agentsMe } = await getMe(this.$axios);

      const danaBalance = danaProfile?.balance || 0;
      const saldoCredits = agentsMe?.dompet?.saldo_credits || 0;
      const saldoDompet = agentsMe?.dompet?.saldo_dompet || 0;

      // Check Eligibility
      const sumAmount = (saldoCredits || 0) + (saldoDompet || 0) + (danaBalance || 0);

      // Eligible if sum Amount is less than Rp500.000 (< Rp500.000)
      const isEligible = sumAmount < 500000;
      commit('successCheckUserSaldo', {
        data: isEligible,
      });

      // Track GMV pre-checking
      trackCpnCheckWalletEligibility({
        eligible: isEligible,
        amount: sumAmount,
        failedReason: isEligible ? '' : 'The Wallet is not eligible',
      });

      return isEligible;
    } catch (error) {
      commit('errorCheckUserSaldo', { error });
      // Track Wallet pre-checking error
      trackCpnCheckWalletEligibility({
        eligible: false,
        failedReason: 'Something went wrong when checking user Wallet',
      });

      throw error;
    }
  },

  /**
   * Check user change credential history
   * @param {object} context
   * @param {object} payload
   * @param {function} payload.trackCpnCheckCredentialChangeEligibility - Tracking function for credential changes eligibility
   * @returns {Promise<boolean>}
   */
  async checkUserCredentialChangesHistory({ commit }, { trackCpnCheckCredentialChangeEligibility = () => {} }) {
    try {
      commit('beginCheckUserCredentialChangesHistory');
      const today = new Date();
      const aWeekAgo = new Date();
      aWeekAgo.setDate(aWeekAgo.getDate() - 7);
      const startDate = `${aWeekAgo.getFullYear()}-${aWeekAgo.getMonth() + 1}-${aWeekAgo.getDate()}`;
      const endDate = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
      await getCredentialChangeStatus(this.$axios, { startDate, endDate });

      commit('successCheckUserCredentialChangesHistory', { data: false });
      /**
       * Track Credential Changes checking - not eligible
       * If there are data from the API, it means the user's account is not eligible
       */
      trackCpnCheckCredentialChangeEligibility({
        eligible: false,
        failedReason: 'Credential Changes is not eligible. There were changes within the last 7 days',
      });

      return false;
    } catch (error) {
      commit('successCheckUserCredentialChangesHistory', { data: true });
      /**
       * Track Credential Changes checking - eligible
       * The API will return error 404 if the history isn't found, therefore the account is eligible
       */
      trackCpnCheckCredentialChangeEligibility({
        eligible: true,
      });

      return false;
    }
  },

  /**
   * Verify identity proofs (KTP, selfie, and supporting document) and automatically change phone number
   * @param {object} context
   * @param {object} payload
   * @param {string} payload.identityCardImage - Base64 image of ktp
   * @param {string} payload.selfPortraitImage - Base64 image of selfie
   * @param {string} payload.supportingDocumentImage - Base64 image of supporting document
   * @param {string} payload.newPhoneNumber - new phone number
   * @param {function} payload.trackCpnAutoChangePhoneNumberAction - tracking function for change phone number action
   */
  async changePhoneNumber(
    { commit },
    {
      identityCardImage = '',
      selfPortraitImage = '',
      supportingDocumentImage = '',
      newPhoneNumber = '',
      trackCpnAutoChangePhoneNumberAction = () => {},
    }
  ) {
    try {
      commit('beginChangePhoneNumber');
      const { data } = await changePhoneNumberUsingIdentityProofs(this.$axios, {
        identity_card_image: identityCardImage.split(',')[1],
        self_portrait_image: selfPortraitImage.split(',')[1],
        supporting_document_image: supportingDocumentImage.split(',')[1],
        new_phone_number: newPhoneNumber,
      });
      commit('successChangePhoneNumber', { data });
      trackCpnAutoChangePhoneNumberAction({
        success: true,
      });

      return true;
    } catch (error) {
      const selectedErrorCause = CPN_ERROR_TYPES.automatic_change_phone_number_error;

      commit('errorChangePhoneNumber', selectedErrorCause);
      trackCpnAutoChangePhoneNumberAction({
        success: false,
        failedReason: selectedErrorCause.message,
      });

      throw new Error(CPN_ERROR_TYPES.automatic_change_phone_number_error.name);
    }
  },

  setSupportingDocumentStatus({ commit }, { status = '' }) {
    commit('setSupportingDocumentStatus', status);
  },

  assignRequestOrigin({ commit }, { origin = '' }) {
    commit('setRequestOrigin', origin);
  },

  async updateConfirmedPhoneNumber(
    { commit, dispatch },
    params = { phoneNumber, changePhoneToken, otpCode, otpMethod }
  ) {
    dispatch('otp/setOtpLoadingState', true, { root: true });
    commit('setPhoneNumberState', { flag: false, message: '' });
    commit('otp/setCurrentNumberValidation', false, { root: true });
    let response;
    try {
      response = await updateConfirmedPhoneNumber(this.$axios, params);
    } catch (err) {
      if (err.response) {
        response = err.response.data;
        const { errors, meta } = err.response.data;
        const otpResponseMessage = errors ? errors[0].message : 'unknown error';
        const isCurrentPhoneNumberValid = otpResponseMessage.includes('Nomor telepon baru tidak valid');
        if (isCurrentPhoneNumberValid) {
          commit('setChangePhoneToken', meta.change_phone_token);
          commit('otp/setCurrentNumberValidation', true, { root: true });
        }

        const isInvalidOtp = otpResponseMessage.includes('Token OTP tidak valid');
        if (isInvalidOtp) dispatch('otp/setOtpValidationState', true, { root: true });

        const otpHasRequested = otpResponseMessage.includes('Token OTP berhasil dikirimkan');
        if (otpHasRequested) {
          dispatch('otp/startOtp', { method: params.otpMethod, timer: meta.wait_time }, { root: true });
          commit('setNewPhoneNumber', params.phoneNumber || '');
        } else commit('setPhoneNumberState', { flag: true, message: otpResponseMessage });
      }
    }
    dispatch('otp/setOtpLoadingState', false, { root: true });
    return response;
  },

  async fetchDanaProfile() {
    try {
      const response = await fetchDanaProfile(this.$axios);
      if (response.meta.http_status === 200) return true;
    } catch (err) {
      if (err) return false;
    }
  },

  async updateDanaBound({ commit }, params = { oldNumber, newNumber }) {
    const response = await updateDanaBoundApi(this.$axios, params);
    commit('setUnbindState', response?.meta?.http_status === 200);
  }
};
